import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

// Inactivity timeout settings (milliseconds)
const INACTIVITY_WARNING_MS = 15 * 60 * 1000;  // 15 min — show warning
const INACTIVITY_LOGOUT_MS  = 20 * 60 * 1000;  // 20 min — auto-logout

// Detect if this window is the messenger popup
const isMessengerPopup = () => window.name === 'zeuscms-messenger';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('zeus_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  const [token, setToken] = useState(() => localStorage.getItem('zeus_token'));
  const isAuthenticated = !!token && !!user;

  // Inactivity state
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const warningTimer = useRef(null);
  const logoutTimer = useRef(null);
  const lastActivity = useRef(Date.now());

  const login = (newToken, userData, refreshToken) => {
    localStorage.setItem('zeus_token', newToken);
    localStorage.setItem('zeus_user', JSON.stringify(userData));
    if (refreshToken) localStorage.setItem('zeus_refresh_token', refreshToken);
    setToken(newToken);
    setUser(userData);
    setSessionExpired(false);
    resetTimers();
  };

  const logout = useCallback(async () => {
    try {
      const rt = localStorage.getItem('zeus_refresh_token');
      if (rt) {
        await fetch(`${process.env.REACT_APP_API_URL || '/api'}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: rt }),
        });
      }
    } catch { /* best-effort */ }
    localStorage.removeItem('zeus_token');
    localStorage.removeItem('zeus_refresh_token');
    localStorage.removeItem('zeus_user');
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);
    setToken(null);
    setUser(null);
    setShowTimeoutWarning(false);

    // In messenger popup, don't redirect — set expired flag instead
    if (isMessengerPopup()) {
      setSessionExpired(true);
    } else {
      window.location.href = '/login';
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await api.get('/auth/me');
      setUser(data);
      localStorage.setItem('zeus_user', JSON.stringify(data));
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  }, []);

  // ── Inactivity timers ─────────────────────────────────────────────────────
  const resetTimers = useCallback(() => {
    lastActivity.current = Date.now();
    setShowTimeoutWarning(false);
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);
    if (!localStorage.getItem('zeus_token')) return;
    warningTimer.current = setTimeout(() => setShowTimeoutWarning(true), INACTIVITY_WARNING_MS);
    logoutTimer.current  = setTimeout(() => logout(), INACTIVITY_LOGOUT_MS);
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const onActivity = () => {
      if (Date.now() - lastActivity.current > 30000) resetTimers();
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    resetTimers();
    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      clearTimeout(warningTimer.current);
      clearTimeout(logoutTimer.current);
    };
  }, [isAuthenticated, resetTimers]);

  useEffect(() => { if (token) refreshUser(); }, []); // eslint-disable-line

  const dismissTimeoutWarning = useCallback(() => {
    setShowTimeoutWarning(false);
    resetTimers();
  }, [resetTimers]);

  return (
    <AuthContext.Provider value={{
      user, token, isAuthenticated,
      login, logout, refreshUser,
      showTimeoutWarning, dismissTimeoutWarning,
      sessionExpired,
    }}>
      {children}

      {/* Session timeout warning overlay */}
      {showTimeoutWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--bg-primary, #fff)', borderRadius: '12px',
            padding: '32px', maxWidth: '400px', width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center',
          }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: 'rgba(234,179,8,0.1)', margin: '0 auto 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                stroke="#eab308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary, #1e293b)' }}>
              Session Expiring Soon
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: '0.88rem', lineHeight: 1.5, color: 'var(--text-secondary, #64748b)' }}>
              You have been inactive for 15 minutes. Your session will end in 5 minutes for security. Click below to continue working.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={logout} style={{
                padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--border, #e2e8f0)',
                background: 'transparent', color: 'var(--text-secondary, #64748b)',
                fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
              }}>Log Out Now</button>
              <button onClick={dismissTimeoutWarning} style={{
                padding: '10px 24px', borderRadius: '8px', border: 'none',
                background: 'var(--accent, #d4a853)', color: '#0a0e17',
                fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
              }}>Continue Session</button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
